import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { CryptoItem } from "../CryptoItem";
import { NotificationsService } from "src/Api/NotificationsService";
import { toast } from "react-toastify";
import { Notification } from "../../types";

const DURATION_OPTIONS = [
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "2 hours", value: 120 },
  { label: "4 hours", value: 240 },
];

interface NotificationListProps {
  notifications: Notification[];
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
}) => {
  const [durations, setDurations] = useState<Record<string, number>>({});

  const handleMarkAsRead = (notification: Notification) => {
    const durationMinutes = durations[notification.id] ?? 15;
    return NotificationsService.markAsRead({ id: notification.id, durationMinutes })
      .then(() => {
        toast(
          `${notification.crypto.name} dismissed for ${durationMinutes} minutes`,
          {
            theme: "dark",
            autoClose: 5000,
            pauseOnFocusLoss: false,
          }
        );
      })
      .catch(() => {
        toast("Error marking notification as read", {
          theme: "dark",
          autoClose: 8000,
          pauseOnFocusLoss: false,
        });
      });
  };

  return (
    <List>
      {notifications.map((n) => (
        <ListItem
          key={n.id}
          alignItems="flex-start"
          divider
          sx={{
            padding: "1rem 1.3rem",
          }}
        >
          <ListItemText
            primary={
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap=".5rem"
              >
                <CryptoItem
                  name={n.crypto?.name}
                  symbol={n.crypto?.symbol}
                  slug={n.crypto?.slug}
                />
              </Box>
            }
            secondary={
              <Box sx={{ marginTop: ".5rem" }}>
                {n.profit != null && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#4caf50", fontWeight: 700, mb: 0.5 }}
                  >
                    ${n.profit.toFixed(2)}{" "}
                    {n.profitPercentage != null && (
                      <span style={{ fontWeight: 400, color: "#aaa" }}>
                        ({n.profitPercentage.toFixed(2)}%)
                      </span>
                    )}
                  </Typography>
                )}
                <Box display="flex" alignItems="center">
                  <ExchangeIcon
                    style={{ height: "1rem" }}
                    name={n.minExchange}
                  />
                  <Typography variant="caption">
                    {n.minExchange} (buy)
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <ExchangeIcon
                    style={{ height: "1rem" }}
                    name={n.maxExchange}
                  />
                  <Typography variant="caption">
                    {n.maxExchange} (sell)
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: getTypeColor(n.type.type) }}>
                  {n.type.type}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Select
                    size="small"
                    value={durations[n.id] ?? 15}
                    onChange={(e) =>
                      setDurations((prev) => ({
                        ...prev,
                        [n.id]: e.target.value as number,
                      }))
                    }
                    sx={{ fontSize: "0.75rem", height: "1.5rem" }}
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            }
          />
          <ListItemSecondaryAction>
            <IconButton
              sx={{ marginRight: "1rem" }}
              edge="end"
              onClick={() => handleMarkAsRead(n)}
            >
              <CheckCircleOutlineOutlined />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};

function getTypeColor(type: string): string {
  switch (type) {
    case "CRITICAL": return "#f44336";
    case "HIGH": return "#ff9800";
    case "IMPORTANT": return "#ffeb3b";
    default: return "#aaa";
  }
}

export default NotificationList;
